import {
  AlignCenterIcon,
  AudienceIcon,
  Column,
  DateRangeObject,
  FontColorEnum,
  FontSizeEnum,
  FontWeightEnum,
  IconCounter,
  IconSizeEnum,
  RecIcon,
  SelectOptionProps,
  SupportRadioIcon,
  SupportTvIcon,
  TablePaginated,
  TablePaginatedOptions,
  Typography,
  ValueIcon,
} from '@acceso/design-system';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { RenderActionButtons } from '@/components/RenderActionButtons';
import { StatusSeenChipInTable } from '@/components/StatusSeenChipInTable';
import { TableRow } from '@/components/TableRow';
import { UserAction } from '@/components/UserAction';
import { useAudienceFilters } from '@/features/audiencias/stores/audience-filters';
import { AugmentedAudience } from '@/features/audiencias/types';
import { usePatchProgramBroadcast } from '@/features/broadcast-schedule-program/api/patch-broadcast-schedule';
import { useChannels } from '@/features/broadcasters/api/get-channels';
import { ChannelLogoSlot } from '@/features/broadcasters/components/ChannelLogoSlot/ChannelLogoSlot';
import { useChannelsByIds } from '@/features/broadcasters/hooks/useChannelsByIds';
import { ChannelMediaTypeEnum } from '@/features/broadcasters/types';
import {
  DEFAULT_CHANNEL_LOGO,
  parentIdsWithoutOwnLogo,
  resolveChannelLogo,
} from '@/features/broadcasters/utils/channel-logo';
import { useProgramViewedPlaceOptions } from '@/features/programs/hooks/useProgramViewedPlaceOptions';
import { DELIVERY_STATES } from '@/helpers/delivery-states';
import { formatDateCustom } from '@/helpers/format-date';
import { getDefaultParrillaDateRange, useParrillaFilters } from '@/stores/parrilla-filters';
import { withAppTzDateFilter } from '@/utils/app-tz-date-range';
import { formatNumberCompact } from '@/utils/money';
import { appWallClockParts } from '@/utils/spain-time';

import { PRIORITY } from '../../constants';
import { TProgramBroadcast } from '../../types';
import { getProgramBroadcastSpecialClassNames } from '../../utils';

import { ProgramBroadcastReviewers } from './components/ProgramBroadcastReviewers';

interface BroadcastScheduleTableProps {
  items: TProgramBroadcast[];
  isFetching?: boolean;
  totalItems?: number;
  onAiRedo: (item: TProgramBroadcast) => void;
  onSegments: (item: TProgramBroadcast) => void;
  onUpdate: (item: TProgramBroadcast) => void;
  onUnlink: (item: TProgramBroadcast) => void;
  onDelete: (item: TProgramBroadcast) => void;
  handleRemoveUserDetailView?: () => void;
  isAudiencesMode: boolean;
}

export function BroadcastScheduleTable({
  items,
  totalItems,
  isFetching,
  onAiRedo,
  onSegments,
  isAudiencesMode,
  onUpdate,
  onUnlink,
  onDelete,
}: BroadcastScheduleTableProps) {
  const { t } = useTranslation();
  const {
    channel_id,
    gte,
    lte,
    paginationValues,
    setFilters,
    setPage,
    setRows,
    setSearchQuery,
    setSortBy,
    clearFilters,
  } = useParrillaFilters();
  const {
    data: { data: channels = [] } = {},
    isSuccess: areChannelsLoaded,
    isFetching: areChannelsFetching,
    isPending: isChannelPending,
  } = useChannels({
    params: {
      programBroadcastStartDatetimeGte: gte,
      programBroadcastStartDatetimeLte: lte,
    },
  });
  const channelsById = useMemo(
    () => new Map(channels.map(channel => [channel.id, channel])),
    [channels]
  );
  // The list above only holds channels with broadcasts in the range, so the
  // parents a logo-less child falls back to are looked up apart when the list
  // lacks them, for the rows on screen only.
  const parentIds = useMemo(
    () =>
      parentIdsWithoutOwnLogo(items.map(item => channelsById.get(item.channel_id))).filter(
        id => !channelsById.has(id)
      ),
    [items, channelsById]
  );
  const { channelsById: lookedUpParentsById, isPending: isParentsPending } =
    useChannelsByIds(parentIds);
  const parentsById = useMemo(
    () => new Map([...channelsById, ...lookedUpParentsById]),
    [channelsById, lookedUpParentsById]
  );
  const { setFilters: setAudienceFilters } = useAudienceFilters();
  const viewedPlaceOptions = useProgramViewedPlaceOptions();
  const { mutate: updateProgramBroadcast } = usePatchProgramBroadcast();
  // Rebuilt only when the Madrid day actually rolls over. The range is handed to
  // the date filter as its initial value, and a fresh object on every render
  // would make the picker discard a selection the user has not applied yet.
  const todayInApp = appWallClockParts(new Date()).date;
  const todayDateRange = useMemo<DateRangeObject>(
    () => getDefaultParrillaDateRange(),
    [todayInApp]
  );

  // Corrects the range when the day rolls over. `gte`/`lte` are read but must
  // stay out of the dependencies: adding them makes this overwrite whatever
  // range the user has just picked with today's.
  useEffect(() => {
    const nextGte = todayDateRange.start?.toISOString();
    const nextLte = todayDateRange.end?.toISOString();

    if (nextGte !== gte || nextLte !== lte) {
      setFilters({ gte: nextGte, lte: nextLte });
    }
  }, [todayDateRange]);

  const channelOptions = useMemo(() => {
    return [{ value: '', label: t('Emisora'), icon: AlignCenterIcon }].concat(
      channels
        .map(channel => ({
          value: channel.id,
          image: channel.parameters?.logo_presigned_url || DEFAULT_CHANNEL_LOGO,
          label: channel.short_name ? `${channel.name} (${channel.short_name})` : channel.name,
          icon: channel.media_type.includes(ChannelMediaTypeEnum.TV)
            ? SupportTvIcon
            : SupportRadioIcon,
        }))
        .sort((a, b) => a.label.localeCompare(b.label))
    ) as SelectOptionProps[];
  }, [channels, t]);

  // Gated on the query having settled: a date change starts a new query whose
  // pending state hands back an empty list, which would otherwise read as
  // "the selection is unsatisfiable" on every date change.
  useEffect(() => {
    if (!channel_id || !areChannelsLoaded || areChannelsFetching) return;

    if (!channels.some(channel => channel.id === channel_id)) {
      setFilters({ channel_id: '' });
    }
  }, [channel_id, channels, areChannelsLoaded, areChannelsFetching]);

  const handleChangeChannel = (value: string) => {
    if (channel_id !== value) {
      setFilters({ channel_id: value });
    }
  };

  const handleChangeStartDate = (value: Date) => {
    if (gte !== value.toISOString()) {
      setFilters({ gte: value.toISOString() });
      setAudienceFilters({
        startDatetimeGte: value.toISOString(),
      });
    }
  };

  const handleChangeEndDate = (value: Date) => {
    if (lte !== value.toISOString()) {
      setFilters({ lte: value.toISOString() });
      setAudienceFilters({
        endDatetimeLte: value.toISOString(),
      });
    }
  };

  const handleChangeMediaType = (value: string | string[]) => {
    if (Array.isArray(value)) return;
    const channelMediaType = value as ChannelMediaTypeEnum;
    setFilters({ channel_media_type: channelMediaType });
    setAudienceFilters({
      mediaTypeIn: [channelMediaType],
    });
  };

  // Remounts the table on "clear all filters". The design system seeds the
  // range it displays from initialValue once and drops it on clean-all, so
  // without this the control reads empty while the grid is on the default
  // range.
  const [filterBarGeneration, setFilterBarGeneration] = useState(0);

  const options: Partial<TablePaginatedOptions> = {
    pagination: {
      changePage: setPage,
      changeRows: setRows,
      currentPage: paginationValues.currentPage,
      itemsPerPage: paginationValues.rowsPerPage,
    },
    onCleanAllFilters: () => {
      clearFilters();
      setFilterBarGeneration(generation => generation + 1);
    },
    query: paginationValues.query,
    setQuery: setSearchQuery,
    setSortBy,
    sortBy: paginationValues.sortBy,
    filters: [
      {
        name: 'broadcaster',
        placeholder: t('Emisora'),
        options: channelOptions,
        // RISK: declared rather than left to the design system, whose default only
        // shows the search above 6 options. This list is the channels of the range
        // on screen, so that rule would make the search come and go with the day.
        withFilter: true,
        onChange: item => {
          handleChangeChannel(item as string);
        },
      },
      {
        name: 'audience_state',
        placeholder: t('Estado audiencia'),
        options: [
          {
            label: t('Cualquier audiencia'),
            value: '',
          },
          {
            label: t('Audiencia asignada'),
            value: true,
          },
          {
            label: t('Audiencia no asignada'),
            value: false,
          },
        ],
        onChange: (item: string | string[] | boolean) => {
          if (item === '') {
            setFilters({
              program_source_id__isnull: null,
            });
            return;
          }

          setFilters({
            program_source_id__isnull: typeof item === 'boolean' ? !item : null,
          });
        },
      },
      {
        name: 'support',
        options: [
          {
            icon: AlignCenterIcon,
            label: t('Soportes'),
            value: '',
          },
          {
            icon: SupportTvIcon,
            label: t('Tv'),
            value: 'tv',
          },
          {
            icon: SupportRadioIcon,
            label: t('Radio'),
            value: 'r',
          },
        ],
        onChange: item => {
          handleChangeMediaType(item);
        },
      },
      {
        name: 'program_viewed_place_id',
        placeholder: t('Lugar de visionado'),
        withFilter: false,
        options: [{ label: t('Lugar de visionado'), value: '' }, ...viewedPlaceOptions],
        onChange: item => {
          setFilters({ program_viewed_place_id: item as string });
        },
      },
    ],
    dateFilter: withAppTzDateFilter({
      className: 'w-64',
      name: 'period',
      initialValue: todayDateRange,
      onChange: item => {
        if (!item?.start && !item?.end) {
          const dateRange = getDefaultParrillaDateRange();

          setFilters({
            gte: dateRange.start.toISOString(),
            lte: dateRange.end.toISOString(),
          });

          return;
        }
        if (item.start) {
          handleChangeStartDate(item.start);
        }

        if (item.end) {
          handleChangeEndDate(item.end);
        }
      },
      showHours: true,
      placeholder: 'Fecha',
    }),
    searchBarPlaceholder: t('Buscar por programa o descripción'),
  };

  const columns: Column<TProgramBroadcast>[] = useMemo(
    () => [
      {
        id: 'priority',
        width: '5%',
        title: '',
        sortable: true,
        valueFn: item => item.priority,
        render: item => {
          return (
            <Typography
              fontSize={FontSizeEnum.Basic}
              weight={FontWeightEnum.Semibold}
              color={FontColorEnum.Dark10}
            >
              {PRIORITY[item.priority - 1].render}
            </Typography>
          );
        },
      },
      {
        id: 'name',
        title: t('Nombre programa'),
        sortable: true,
        valueFn: item => item.name || '',
        render: item => {
          const channel = channelsById.get(item.channel_id);

          return (
            <div className="flex items-center gap-1">
              {channel?.media_type.includes(ChannelMediaTypeEnum.TV) ? (
                <SupportTvIcon />
              ) : (
                <SupportRadioIcon />
              )}
              <TableRow title={item.name || ''} className="!font-semibold" />
            </div>
          );
        },
      },
      {
        id: 'description',
        title: t('Descripción'),
        sortable: true,
        isVisible: !isAudiencesMode,
        valueFn: item => item.description || '',
        render: item => {
          return <TableRow content={item.description || ''} />;
        },
      },
      {
        id: 'channel',
        title: t('Emisora'),
        sortable: true,
        valueFn: item => item.channel_id || '',
        render: item => {
          const channel = channelsById.get(item.channel_id);
          const logo = resolveChannelLogo(channel, {
            parentsById,
            isParentsPending,
            isChannelPending,
          });

          return (
            <div className="flex items-center gap-2">
              <ChannelLogoSlot source={logo} alt={channel?.short_name || ''} />
              <TableRow content={channel?.short_name || ''} />
            </div>
          );
        },
      },
      {
        id: 'start_datetime',
        title: t('Fecha y hora'),
        sortable: true,
        valueFn: item => item.start_datetime,
        render: item => {
          return (
            <div className="flex flex-col items-start gap-1">
              <Typography fontSize={FontSizeEnum.Basic}>
                {formatDateCustom({ input: item?.start_datetime, format: 'dd/MM/yyyy' })}
              </Typography>
              <Typography fontSize={FontSizeEnum.Basic} weight={FontWeightEnum.Semibold}>
                {formatDateCustom({
                  input: item.start_datetime,
                  format: 'HH:mm',
                })}{' '}
                - {formatDateCustom({ input: item.end_datetime, format: 'HH:mm' })}
              </Typography>
            </div>
          );
        },
      },
      {
        id: 'data',
        title: t('Datos'),
        valueFn: item => item.start_datetime,
        render: item => {
          return (
            <div className="flex flex-wrap items-center gap-1">
              {item.audience !== null && (
                <IconCounter
                  icon={AudienceIcon}
                  count={`${formatNumberCompact(item.audience)} / ${
                    item.audience_rating_percent ? item.audience_rating_percent.toFixed(2) : '0.00'
                  } %`}
                  className="shrink-0"
                />
              )}
              {item.economical_value !== null && (
                <IconCounter
                  icon={ValueIcon}
                  count={`${formatNumberCompact(item.economical_value)} €`}
                  className="shrink-0"
                />
              )}
            </div>
          );
        },
      },
      {
        id: 'delivery_datetime',
        title: t('Entrega'),
        sortable: true,
        isVisible: !isAudiencesMode,
        valueFn: item => item.delivery_datetime || '',
        render: item => {
          const deliverySelected = DELIVERY_STATES.find(
            delivery => delivery.value === item.delivery_datetime_at
          );
          return (
            <div className="flex items-center gap-1">
              {deliverySelected && <deliverySelected.icon size={IconSizeEnum.SM} />}
              <TableRow
                className={deliverySelected?.color}
                content={formatDateCustom({
                  input: item?.delivery_datetime || '',
                  format: 'HH:mm:ss',
                })}
              />
            </div>
          );
        },
      },
      {
        id: 'review_state',
        title: t('Estado'),
        sortable: true,
        isVisible: !isAudiencesMode,
        valueFn: item => item.review_state || '',
        render: item => {
          const isInStreaming =
            new Date() >= new Date(item.start_datetime) &&
            new Date() <= new Date(item.end_datetime);

          return (
            <div className="flex gap-2">
              <StatusSeenChipInTable
                classname="w-fit"
                review_state={item.review_state}
                item={item}
              />
              {isInStreaming && <RecIcon className="animate-fade-in-out !text-feedback-error-20" />}
              <ProgramBroadcastReviewers programBroadcastId={item?.id || ''} />
            </div>
          );
        },
      },
      {
        id: 'updated_by',
        title: t('Actualizado por'),
        sortable: true,
        valueFn: item => item.updated_at ?? '',
        render: item => {
          if (!item.updated_at) return null;
          return <UserAction id={item.updated_by} timestamp={item.updated_at} />;
        },
      },
      {
        id: 'actions',
        title: '',
        className: 'items-end',
        width: '5%',
        render: item => {
          if (isAudiencesMode) {
            return (
              <RenderActionButtons
                item={item}
                onUnLink={onUnlink}
                onUpdate={onUpdate}
                tooltips={{
                  unlink: t('Desvincular audiencia'),
                }}
              />
            );
          }

          return (
            <RenderActionButtons
              item={item}
              onSegments={() => onSegments(item)}
              onAiRedo={() => onAiRedo(item)}
              onUpdate={onUpdate}
              onDelete={onDelete}
              tooltips={{
                segments: t('Ir a la parrilla de segmentos'),
              }}
            />
          );
        },
      },
    ],
    [
      t,
      channelsById,
      parentsById,
      isParentsPending,
      isChannelPending,
      items,
      onSegments,
      onAiRedo,
      onUpdate,
      onUnlink,
      isAudiencesMode,
    ]
  );

  return (
    <TablePaginated<TProgramBroadcast, AugmentedAudience>
      key={filterBarGeneration}
      isFetching={isFetching}
      totalItems={totalItems || 0}
      options={options}
      columns={columns}
      data={items}
      showFilter
      ignoreEmpty
      ignoreWrap
      empty_state={{
        filterTitle: t('Vaya no hemos encontrado nada'),
        filterSubtitle: t('Prueba con otros criterios de búsqueda'),
      }}
      onDrop={data => {
        updateProgramBroadcast({
          id: data.item.id,
          audience_program_source_id: data.droppedItem.program_source_id,
          audience: data.droppedItem.data.value,
          audience_rating_percent: data.droppedItem.audience_rating_percent,
          start_datetime: data.droppedItem.start_datetime,
          end_datetime: data.droppedItem.end_datetime,
          name: data.droppedItem.program_source?.name,
        });
      }}
      getSpecialClassNames={getProgramBroadcastSpecialClassNames}
    />
  );
}
